/**
 * User-facing message for a rejected `/api/report-generation/jobs` enqueue.
 *
 * The route answers a rejection with a headline (`error`), optionally the
 * machine code (`code`) of a `ReportGenerationEnqueueError`, and for schema
 * failures `details: Array<{path, message}>`. The store used to keep the
 * headline + details and DROP the code, so a parent-report rejection reached
 * the user as raw server prose with no next step. The formatting lives here,
 * as one pure function, so the store and its tests cannot drift apart.
 */
export interface EnqueueRejectionBody {
  error?: string;
  code?: string;
  details?: unknown;
}

/** Codes whose rejection the user can act on from the report list. */
const GUIDANCE_BY_CODE: Record<string, string> = {
  PARENT_REPORT_NOT_FOUND:
    'The report you chose to update could not be resolved for your account. Refresh the Analyses list and pick a current report, or generate a new report.',
  PARENT_REPORT_MISMATCH:
    'The report you chose to update no longer matches this site and report type, or it belongs to another user and cannot be replaced. Use Update instead of Replace, or generate a new report.',
  ENQUEUE_RECEIPT_CORRUPT:
    'This request references a previous generation that is no longer intact. Start the generation again.',
  LINEAGE_CORRUPT:
    'The version history for this report is inconsistent. Generate a new report instead of updating this one.',
  // P2.1 — the chokepoint's 409 used to land as prose with no next step.
  // Names the production tab label. With VITE_DECISION_FINDINGS_V1 rolled back
  // the same tab is labelled "Reports"; production ships it on
  // (cloudbuild.yaml `_DECISION_FINDINGS_V1: 'true'`), so the rollback label
  // is accepted here rather than making this constant flag-aware.
  ANALYSIS_PREREQUISITE_NOT_READY:
    'This analysis is not ready to generate yet. Open the Analyses tab: the analysis is listed there as needing setup, with the action that resolves its prerequisite.',
  // P1.9 — refused at enqueue instead of failing at dispatch.
  PROVIDER_COVERAGE_BLOCKED:
    'A required data provider returned nothing for this site, so the analysis would fail at dispatch. Re-run the SED bootstrap if the outage was transient; otherwise ask an admin for a coverage waiver.',
  // P1.8 — the platform is US-only for now.
  OUTSIDE_SUPPORTED_TERRITORY:
    'This site is outside the platform\'s supported territory (United States) for this analysis. Choose a US site, or an analysis whose evidence providers cover this location.',
  // P2.2 — the report type's status is now checked server-side.
  REPORT_TYPE_NOT_DISPATCHABLE:
    'This report type cannot be generated: it is either a live decision surface the site keeps current on its own (nothing to generate) or not released yet. Choose another report type.',
};

export function formatEnqueueRejection(status: number, body: EnqueueRejectionBody): string {
  const headline = body.error ?? `Enqueue failed (${status})`;
  // Bounded on purpose: this string reaches a toast and a panel, so a
  // 40-issue Zod dump would be as unreadable as showing nothing.
  const detail = Array.isArray(body.details)
    ? body.details
      .map((issue) => {
        // Guard the row itself: a null/primitive entry in `details` would
        // otherwise throw while building an ERROR message, replacing a
        // useful diagnosis with an unrelated TypeError.
        if (typeof issue !== 'object' || issue === null) return null;
        const row = issue as { path?: unknown; message?: unknown };
        const path = typeof row.path === 'string' && row.path.length > 0 ? row.path : null;
        const message = typeof row.message === 'string' ? row.message : null;
        if (!message) return null;
        return path ? `${path}: ${message}` : message;
      })
      .filter((line): line is string => line !== null)
      .slice(0, 4)
      .join('; ')
    : '';
  const guidance = typeof body.code === 'string' ? GUIDANCE_BY_CODE[body.code] : undefined;
  const base = detail ? `${headline} — ${detail}` : headline;
  return guidance ? `${base} ${guidance}` : base;
}
